"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { Calendar, ArrowRight, Image as ImageIcon, CircleAlert as AlertCircle } from 'lucide-react';
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
function ZonaPPNewsGridConsumer() {
  const {
    posts,
    loading,
    error
  } = usePosts();
  if (loading) {
    return <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
        {[...Array(6)].map((_, i) => <Card key={i} data-index={i} className="overflow-hidden rounded-xl border-border shadow-sm">
            <Skeleton className="aspect-[16/9] w-full" />
            <CardContent className="p-6">
              <Skeleton className="h-4 w-24 mb-4" />
              <Skeleton className="h-6 w-full mb-2" />
              <Skeleton className="h-6 w-2/3 mb-4" />
              <Skeleton className="h-4 w-full mb-2" />
              <Skeleton className="h-4 w-4/5" />
            </CardContent>
          </Card>)}
      </div>;
  }
  if (error) {
    return <Alert variant="destructive" className="max-w-2xl mx-auto">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Gagal memuat berita</AlertTitle>
        <AlertDescription>
          Terjadi kesalahan saat mengambil data berita terbaru. Silakan muat ulang halaman.
        </AlertDescription>
      </Alert>;
  }
  if (!posts || posts.length === 0) {
    return <div className="text-center py-16 bg-muted/30 rounded-2xl border border-dashed border-border">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-lg bg-muted mb-4">
          <ImageIcon className="size-8 text-muted-foreground" />
        </div>
        <h3 className="text-xl font-bold font-default mb-2">Belum Ada Berita</h3>
        <p className="text-muted-foreground max-w-md mx-auto">
          Saat ini belum ada berita terbaru di kategori Zona PP. Silakan kembali lagi nanti.
        </p>
      </div>;
  }
  return <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
      {posts.map((post, i) => <Card key={post.id} data-index={i} className="overflow-hidden rounded-xl border border-border shadow-sm hover:shadow-md transition-all duration-300 group flex flex-col h-full relative">
          <div className="relative aspect-[16/9] overflow-hidden bg-muted shrink-0">
            {post.featuredImage ? <img src={post.featuredImage} alt={post.title} className="object-cover w-full h-full group-hover:scale-105 transition-transform duration-500" loading="lazy" /> : <div className="w-full h-full flex items-center justify-center bg-muted text-muted-foreground">
                <ImageIcon className="size-10 opacity-20" />
              </div>}
            <div className="absolute top-4 left-4 z-10">
              <Badge className="bg-[var(--pp-gold)] text-black hover:bg-[var(--pp-gold)]/90 border-none font-bold shadow-sm">
                Zona PP
              </Badge>
            </div>
          </div>
          
          <CardContent className="p-6 flex-1 flex flex-col">
            <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground mb-3 uppercase tracking-wider">
              <SafeHydrate><Calendar className="size-3.5" /></SafeHydrate>
              <time dateTime={post.date}>
                {post.date ? new Date(post.date).toLocaleDateString('id-ID', {
              day: 'numeric',
              month: 'long',
              year: 'numeric'
            }) : ''}
              </time>
            </div>
            
            <h3 className="text-xl font-bold font-default leading-snug mb-3 line-clamp-2 group-hover:text-[var(--pp-red)] transition-colors">
              <Link to={post.link || "#"} className="before:absolute before:inset-0 z-0">
                {post.title}
              </Link>
            </h3>
            
            {post.excerpt && <div className="text-muted-foreground text-sm line-clamp-3 font-sans flex-1 mb-4" dangerouslySetInnerHTML={{
          __html: post.excerpt
        }} />}
            
            <div className="mt-auto pt-4 border-t border-border/50 flex items-center text-sm font-semibold text-[var(--pp-red)] group-hover:text-[var(--pp-red)]/80 transition-colors">
              Baca Selengkapnya
              <ArrowRight className="ml-1.5 size-4 transition-transform group-hover:translate-x-1" />
            </div>
          </CardContent>
        </Card>)}
    </div>;
}
export default function ZonaPPNewsGrid() {
  const wp_query_params = {
    post_type: "post",
    per_page: 6,
    orderby: "date",
    order: "desc"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="berita" className="bg-background py-16 md:py-24">
      <div className="container mx-auto px-4 md:px-6">
        <div className="mb-10 md:mb-14 flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div className="max-w-2xl">
            <h2 className="text-3xl md:text-4xl font-bold font-default uppercase text-foreground border-l-4 pl-4 md:pl-5 leading-tight" style={{
            borderColor: 'var(--pp-red)'
          }}>
              Berita Organisasi Terbaru
            </h2>
            <p className="text-muted-foreground mt-4 pl-4 md:pl-5 text-lg">
              Kabar terkini, kegiatan, dan program kerja dari berbagai tingkatan organisasi Pemuda Pancasila.
            </p>
          </div>
          
          <Link to="/NewsPage" className="shrink-0">
            <Button variant="outline" className="w-full md:w-auto border-[var(--pp-red)] text-[var(--pp-red)] hover:bg-[var(--pp-red)] hover:text-white transition-colors">
              Lihat Semua Berita
              <ArrowRight className="ml-2 size-4" />
            </Button>
          </Link>
        </div>

        <PostsProvider wp_query={{
        post_type: "post",
        per_page: 6,
        orderby: "date",
        order: "desc"
      }}>
          <ZonaPPNewsGridConsumer />
        </PostsProvider>
      </div>
    </section>;
}